import React, { useState } from "react";
import { BaseTable } from "@app/components/common/BaseTable/BaseTable";
import { BaseButton } from "@app/components/common/BaseButton/BaseButton";
import { BaseSpace } from "@app/components/common/BaseSpace/BaseSpace";
import { PageTitle } from "@app/components/common/PageTitle/PageTitle";
import { message, Input, Select, Switch, Form, Row, Col, Alert, Modal, Checkbox, Spin } from "antd";
import { SearchOutlined, SafetyOutlined, KeyOutlined, UndoOutlined } from "@ant-design/icons";
import { adminApi, User, Role } from "@app/api/admin";
import { initialPagination, initialPaginationProps } from "./CompanyPage";
import { useUpdateEffect } from "@app/hooks/useUpdateEffect";
import dayjs from "dayjs";
import { ColumnsType } from "antd/lib/table";

const UserPage: React.FC = () => {
  const [tableData, setTableData] = useState<{
    data: User[];
    pagination: initialPaginationProps;
  }>({
    data: [],
    pagination: initialPagination,
  });
  const [loading, setLoading] = useState(false);

  // 使用 Form 实例管理表单
  const [searchForm] = Form.useForm();

  // 使用统一的 searchParams 状态替代分散的状态
  const [searchParams, setSearchParams] = useState<{
    keyword?: string;
    status?: number;
    is_admin?: boolean;
  }>({});

  // 添加独立的分页状态
  const [pagination, setPagination] = useState(initialPagination);

  // 角色管理相关状态
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [initialUserRoles, setInitialUserRoles] = useState<string[]>([]);
  const [roleLoading, setRoleLoading] = useState(false);

  // 重置密码相关状态
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordForm] = Form.useForm();
  const [passwordLoading, setPasswordLoading] = useState(false);

  // 获取所有角色
  const fetchAllRoles = async () => {
    try {
      const { data } = await adminApi.roles.list({ page: 1, page_size: 100 });
      setAllRoles(data.list || []);
    } catch (error: any) {
      console.error("获取角色列表失败", error);
    }
  };

  // 管理角色
  const handleManageRoles = async (record: User) => {
    setCurrentUserId(record.id);
    setRoleLoading(true);
    setRoleModalVisible(true);
    
    if (allRoles.length === 0) {
      await fetchAllRoles();
    }

    try {
      const { data } = await adminApi.roles.getUserRoles(record.id);
      setUserRoles(data.roles || []);
      setInitialUserRoles(data.roles || []);
    } catch (error: any) {
      console.error("获取用户角色失败", error);
      setUserRoles([]);
      setInitialUserRoles([]);
    } finally {
      setRoleLoading(false);
    }
  };

  // 提交角色更改
  const handleRoleSubmit = async () => {
    if (!currentUserId) return;

    try {
      setRoleLoading(true);
      const rolesToAdd = userRoles.filter(role => !initialUserRoles.includes(role));
      const rolesToRemove = initialUserRoles.filter(role => !userRoles.includes(role));

      if (rolesToAdd.length > 0) {
        await adminApi.roles.assignMultipleRoles(currentUserId, rolesToAdd);
      }

      if (rolesToRemove.length > 0) {
        await adminApi.roles.revokeMultipleRoles(currentUserId, rolesToRemove);
      }

      message.success("角色更新成功");
      setRoleModalVisible(false);
      fetchUsers();
    } catch (error: any) {
      message.error(error.message || "角色更新失败");
    } finally {
      setRoleLoading(false);
    }
  };

  // 打开重置密码弹窗
  const handleResetPassword = (record: User) => {
    setCurrentUserId(record.id);
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  // 提交重置密码
  const handlePasswordSubmit = async () => {
    if (!currentUserId) return;

    try {
      const values = await passwordForm.validateFields();
      setPasswordLoading(true);
      
      await adminApi.users.resetPassword(currentUserId, { new_password: values.new_password });

      message.success("密码重置成功");
      setPasswordModalVisible(false);
    } catch (error: any) {
      message.error(error.message || "密码重置失败");
    } finally {
      setPasswordLoading(false);
    }
  };



  // 获取用户列表数据 - 简化函数，不接受参数
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { total, ...rest } = pagination;
      const { data } = await adminApi.users.list({
        ...rest,
        ...searchParams,
      });

      setTableData({
        data: data.users || [],
        pagination: data.pagination,
      });
    } catch (error: any) {
      console.error(error.message || "获取用户列表失败");
    } finally {
      setLoading(false);
    }
  };

  // 监听搜索条件和分页变化
  useUpdateEffect(() => {
    fetchUsers();
  }, [searchParams, pagination]);

  // 搜索处理
  const handleSearch = (values: any) => {
    setSearchParams(values);
    setPagination({ ...initialPagination, page: 1 });
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({});
    setPagination({ ...initialPagination, page: 1 });
  };

  // 状态切换
  const handleStatusToggle = async (record: User, checked: boolean) => {
    try {
      const newStatus = checked ? 1 : 0;
      await adminApi.users.updateStatus(record.id, newStatus);

      fetchUsers();

      message.success(`用户状态已${checked ? "启用" : "禁用"}`);
    } catch (error: any) {
      message.error(error.message || "状态更新失败");
    }
  };

  const handleTableChange = (page: number, pageSize: number) => {
    setPagination({
      page,
      page_size: pageSize,
    });
  };

  const columns: ColumnsType<User> = [
    {
      title: "用户名",
      dataIndex: "username",
      width: 120,
    },
    // {
    //   title: "昵称",
    //   dataIndex: "nickname",
    //   width: 120,
    // },
    {
      title: "头像",
      dataIndex: "avatar",
      width: 80,
      render: (avatar: string) => avatar ? (
        <img
          src={avatar}
          alt="avatar"
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
          }}
        />
      ) : null
    },
    {
      title: "邮箱",
      dataIndex: "email",
      width: 160,
    },
    {
      title: "手机号",
      dataIndex: "phone",
      width: 120,
    },
    // 性别
    {
      title: "性别",
      dataIndex: "gender",
      width: 80,
      render: (gender: number) => gender ? (gender === 1 ? "男" : "女") : "未知",
    },
    // 生日
    {
      title: "生日",
      dataIndex: "birthday",
      width: 120,
      render: (birthday: string) => birthday ? dayjs(birthday).format("YYYY-MM-DD") : "",
    },
    
    // {
    //   title: "管理员",
    //   dataIndex: "is_admin",
    //   width: 80,
    //   render: (isAdmin: boolean) => <span>{isAdmin ? "是" : "否"}</span>,
    // },
    {
      title: "状态",
      dataIndex: "status",
      width: 80,
      render: (status: number, record: User) => (
        <Switch
          checked={status === 1}
          onChange={(checked) => handleStatusToggle(record, checked)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
        />
      ),
    },
    {
      title: "最后登录时间",
      dataIndex: "last_login_at",
      width: 140,
      render: (lastLoginAt: string) => lastLoginAt ? dayjs(lastLoginAt).format("YYYY-MM-DD HH:mm:ss") : "",
    },
    {
      title: "操作",
      key: "action",
      width: 120,
      fixed: "right",
      render: (_: any, record: User) => (
        <BaseSpace>
        
          <BaseButton 
            type="primary" 
            size="small" 
            danger
            icon={<UndoOutlined />}
            onClick={() => handleResetPassword(record)}
          >
            重置密码
          </BaseButton>
        </BaseSpace>
      ),
    },
  ];

  return (
    <>
      <PageTitle>用户管理</PageTitle>
      <Alert
        type="info"
        message="页面功能说明"
        description="此页面展示在所有在小程序中注册登录的用户"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* 搜索表单 */}
      <Form
        form={searchForm}
        onFinish={handleSearch}
        layout="inline"
        style={{
          marginBottom: 16,
        }}
      >
        <Row gutter={[16, 16]} style={{ width: "100%" }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="search" style={{ marginBottom: 0 }}>
              <Input placeholder="用户名/手机号" allowClear />
            </Form.Item>
          </Col>
 

          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="status" style={{ marginBottom: 0 }}>
              <Select placeholder="状态" allowClear style={{ width: "100%" }}>
                <Select.Option value={1}>启用</Select.Option>
                <Select.Option value={0}>禁用</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          {/* <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="is_admin" style={{ marginBottom: 0 }}>
              <Select placeholder="管理员" allowClear style={{ width: "100%" }}>
                <Select.Option value={true}>是</Select.Option>
                <Select.Option value={false}>否</Select.Option>
              </Select>
            </Form.Item>
          </Col> */}

          <Col xs={24} sm={24} md={8} lg={6}>
            <Form.Item style={{ marginBottom: 0 }}>
              <BaseSpace>
                <BaseButton
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  搜索
                </BaseButton>
                <BaseButton onClick={handleReset}>重置</BaseButton>
              </BaseSpace>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <BaseTable
        bordered
        dataSource={tableData.data}
        columns={columns}
        pagination={{
          ...tableData.pagination,
          onChange: handleTableChange,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
        }}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1600 }}
      />

      {/* 角色管理模态框 */}
      <Modal
        title="管理用户角色"
        visible={roleModalVisible}
        onOk={handleRoleSubmit}
        onCancel={() => setRoleModalVisible(false)}
        confirmLoading={roleLoading}
        width={600}
        destroyOnClose
      >
        <Spin spinning={roleLoading}>
          <div style={{ marginBottom: 16 }}>
            <p style={{ marginBottom: 8, color: "#666" }}>
              请选择要分配给用户的角色：
            </p>
            {allRoles.length > 0 ? (
              <Checkbox.Group
                value={userRoles}
                onChange={(checkedValues) => setUserRoles(checkedValues as string[])}
                style={{ width: '100%' }}
              >
                <Row>
                  {allRoles.map(role => (
                    <Col span={8} key={role.id}>
                      <Checkbox value={role.name}>{role.display_name || role.name}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            ) : (
              <div style={{ color: "#999", textAlign: "center", padding: "20px" }}>
                暂无可用角色
              </div>
            )}
          </div>
        </Spin>
      </Modal>

      {/* 重置密码模态框 */}
      <Modal
        title="重置用户密码"
        visible={passwordModalVisible}
        onOk={handlePasswordSubmit}
        onCancel={() => setPasswordModalVisible(false)}
        confirmLoading={passwordLoading}
        destroyOnClose
      >
        <Form form={passwordForm} layout="vertical" initialValues={{ new_password: "" }}>
          <Alert
            message="提示"
            description="如果不输入新密码，将默认重置为 123456"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              { min: 6, message: "密码长度至少为6位" }
            ]}
          >
            <Input.Password placeholder="请输入新密码（可选）" />
          </Form.Item>
        </Form>
      </Modal>

     
    </>
  );
};

export default UserPage;

export const useUserList = () => {
  const [userList, setUserList] = useState<User[]>([]);

  const fetchUsers = async () => {
    try {
      const { data } = await adminApi.users.list({
        page: 1,
        page_size: 100,
      });
      setUserList(data.users || []);
    } catch (error: any) {
      console.error(error.message || "获取用户列表失败");
    }
  };
  
  useUpdateEffect(() => {
    fetchUsers();
  }, []);

  return {
    userList,
    fetchUsers,
  }
};

